/**
 * The address a renter is actually sent.
 *
 * WHY A TOKEN AND NOT THE QUOTE'S REFERENCE
 *
 * `Q-2124-1` is the reference on purpose: a person can say it down a phone, it
 * sorts, and two of them obviously belong to the same renter. Every one of
 * those properties is a reason it must never be the thing that opens the page —
 * the second quote of lead 2125 is `Q-2125-2` and anybody who has seen one link
 * can type the next.
 *
 * So the URL carries 192 bits from the platform's CSPRNG and means nothing. It
 * is not derived from the quote, the lead, the date or the price, because a
 * token you can DERIVE is a token you can guess.
 *
 * THE ALPHABET IS URL-SAFE BASE64 WITHOUT THE PADDING, which is what
 * `crypto.randomUUID` would have given us four times over with hyphens in it.
 * Nothing here needs to be read aloud, so legibility is not a design goal;
 * surviving a copy-paste out of an email client is, and that is what the
 * alphabet is chosen for.
 */

import { RENTER_BASE } from '../shared/hosts.js'

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_'

/** 32 characters, 192 bits. The migration refuses anything shorter than 24. */
export const TOKEN_LENGTH = 32

/**
 * `crypto` is a parameter so a test can stand where the platform stands.
 *
 * It is NOT defaulted to `Math.random` when the platform has no CSPRNG. A
 * weaker token that still looks like a token is the worst outcome available
 * here: it fails silently, forever, and nothing on any screen says so. A
 * platform that cannot produce randomness cannot produce a link.
 */
export function newLinkToken(crypto = globalThis.crypto) {
  if (!crypto?.getRandomValues) {
    throw new Error('this browser has no secure random source, so it cannot make a quote link')
  }
  const bytes = crypto.getRandomValues(new Uint8Array(TOKEN_LENGTH))
  let out = ''
  for (const b of bytes) out += ALPHABET[b & 63]
  return out
}

/** Where a link lives. One path, so the server and the app cannot disagree. */
export const QUOTE_LINK_PATH = '/q/'

/**
 * THE HOST A RENTER IS SENT TO, AND WHY IT IS A CONSTANT RATHER THAN THE WINDOW'S.
 *
 * This used to be `location.origin` — the address of whatever window the agent had open. It
 * read as flexible and it was a failure that reports nothing: an agent still on the old
 * `snowball-mtr-production.up.railway.app` bookmark sends a renter a link on that host, the
 * page loads perfectly, and nobody finds out. No error, no log line, nothing in the sent
 * record that says which host went out.
 *
 * Two things were wrong with that at once, and the second is the one that costs money:
 *
 *   · DEC-084 — mid-term rental is Stayoo. A link is the most visible surface we have, and
 *     `snowball-mtr-production` in it is the short-term brand on a Stayoo page.
 *   · A renter who was just warned by Zillow not to click links in messages is then sent a
 *     text signed "Stayoo" pointing at a hostname that matches nothing she has been told.
 *     That is a conversion problem before it is a branding one.
 *
 * So the base is fixed here and `quoteLinkUrl` takes no origin. There is nothing left to pass
 * it, which is the point: a function that cannot be given a host cannot be given a wrong one.
 *
 * THE ACCEPTED COST, stated rather than discovered later: a copy of this app running anywhere
 * else — a laptop, a future staging environment — still mints production links. Pressing send
 * on a development machine reaches the real renter's real quote. If a staging environment is
 * ever built, this constant is the thing it has to solve, and it should be solved by an
 * environment variable read ONCE here, never by handing the origin back to the caller.
 */
/* The renters' address (go.stayoo.com once switched on) — every link we send. See shared/hosts.js. */
export const PUBLIC_BASE = RENTER_BASE

export const quoteLinkUrl = (token) => `${PUBLIC_BASE}${QUOTE_LINK_PATH}${token}`

/**
 * The token in an address bar, or null.
 *
 * Deliberately strict about the alphabet: a path that is nearly a token is a
 * typo or a truncated paste, and answering it with "we could not find that
 * quote" is a better sentence than a database round trip that says the same
 * thing more slowly.
 */
export function tokenFromPath(pathname) {
  const m = /^\/q\/([A-Za-z0-9_-]{24,64})\/?$/.exec(String(pathname ?? ''))
  return m ? m[1] : null
}

/**
 * How long a link keeps working — 30 days, and NOT the 24 hours the prices get.
 *
 * They are two different promises. The price lapses because a price lapses; the
 * link stays up because the alternative is a renter who opened the email on
 * Saturday being told by a 404 that we lost her quote. The page has a truthful
 * thing to say about a lapsed price and nothing truthful to say about a dead
 * link, so the link outlives the price by a wide margin.
 */
export const LINK_LIFETIME_MS = 30 * 24 * 60 * 60 * 1000

export const linkExpiryFrom = (sentAt) =>
  new Date(Date.parse(sentAt) + LINK_LIFETIME_MS).toISOString()
